import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";

export interface PlatformPageProps {
  /** Page title rendered in the header (h1). */
  title: string;
  /** Optional supporting copy beneath the title. */
  description?: string;
  /** Renders a centered loading indicator instead of children. */
  isLoading?: boolean;
  /** Renders a generic, honest error state instead of children. */
  isError?: boolean;
  /** Renders an empty state instead of children. */
  isEmpty?: boolean;
  /** Message shown in the empty state. */
  emptyMessage?: string;
  /** Header actions rendered beside the title at wider breakpoints. */
  actions?: ReactNode;
  /** The real page content, shown only when no state flag applies. */
  children?: ReactNode;
}

/**
 * Standard Platform page composition with exactly one body branch.
 *
 * Precedence: error > loading > empty > content. The useful page heading,
 * supporting description, and supplied actions stay available in every state.
 */
export function PlatformPage({
  title,
  description,
  isLoading = false,
  isError = false,
  isEmpty = false,
  emptyMessage,
  actions,
  children,
}: PlatformPageProps) {
  let body: ReactNode;

  if (isError) {
    body = (
      <Alert severity="error" role="alert">
        Failed to load this page. Try again shortly.
      </Alert>
    );
  } else if (isLoading) {
    body = (
      <Box
        role="status"
        aria-label={`Loading ${title}`}
        aria-live="polite"
        sx={{ display: "flex", justifyContent: "center", py: 8 }}
      >
        <CircularProgress size={36} />
      </Box>
    );
  } else if (isEmpty) {
    body = (
      <Box role="status" aria-live="polite" sx={{ py: 6, textAlign: "center" }}>
        <Typography color="text.secondary">
          {emptyMessage ?? "Nothing here yet"}
        </Typography>
      </Box>
    );
  } else {
    body = children;
  }

  return (
    <Box>
      <Stack
        component="header"
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{
          alignItems: { xs: "stretch", sm: "flex-start" },
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          {description && (
            <Typography color="text.secondary" sx={{ mt: 0.75 }}>
              {description}
            </Typography>
          )}
        </Box>
        {actions && <Box sx={{ flexShrink: 0 }}>{actions}</Box>}
      </Stack>
      {body}
    </Box>
  );
}
